import React from 'react';
import PropTypes from 'prop-types';
import { EllipsisVertical, Check, Edit, Trash } from 'lucide-react';

export default function EncyclopediaRowActions({
  onAccept,
  onEdit,
  onDelete,
  menuWidth = 200,
  attachTo,
}) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const placeMenu = React.useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;

    const M = 8; // viewport margin
    const left = Math.min(
      window.innerWidth - menuWidth - M,
      Math.max(M, r.right - menuWidth) // right-align to button
    );
    const top = Math.min(window.innerHeight - M, r.bottom + 8);

    setPos({ top, left });
  }, [menuWidth]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) placeMenu();
    setOpen((s) => !s);
  };

  // Close on outside click / ESC; keep position in sync on scroll/resize
  React.useEffect(() => {
    const onDocClick = (ev) => {
      if (!open) return;
      if (btnRef.current?.contains(ev.target)) return;
      if (menuRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    const onReflow = () => open && placeMenu();

    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);

    let attachEl;
    if (attachTo) {
      attachEl = document.querySelector(attachTo);
      attachEl && attachEl.addEventListener('scroll', onReflow, { passive: true });
    }

    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
      attachEl && attachEl.removeEventListener('scroll', onReflow);
    };
  }, [open, placeMenu, attachTo]);

  return (
    <div className="row-actions">
      <button
        ref={btnRef}
        type="button"
        className="row-actions__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <EllipsisVertical className="row-actions__icon" />
      </button>

      {open && (
        <div
          ref={menuRef}
          className="row-actions__menu row-actions__menu--fixed"
          role="menu"
          style={{ top: pos.top, left: pos.left, width: menuWidth }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="row-actions__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              if (onAccept) onAccept();
            }}
          >
            <Check className="row-actions__icon" size={16} />
            <span>Zaakceptuj zmiany</span>
          </button>

          <button
            className="row-actions__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              if (onEdit) onEdit();
            }}
          >
            <Edit className="row-actions__icon" size={16} />
            <span>Edytuj</span>
          </button>

          <button
            className="row-actions__item row-actions__item--danger"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              if (onDelete) onDelete();
            }}
          >
            <Trash className="row-actions__icon" size={16} />
            <span>Usuń</span>
          </button>
        </div>
      )}
    </div>
  );
}

EncyclopediaRowActions.propTypes = {
  onAccept: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  menuWidth: PropTypes.number,
  attachTo: PropTypes.string,
};




